import React, { useState, useEffect, useRef } from 'react';
import { api } from '../services/api';
import { streamImport } from '../services/import';
import { dayToDayAccounts } from '../utils/accounts';
import { ensureSubject } from '../utils/taxonomy';
import SearchableSelect from './SearchableSelect';
import Select from './Select';
import '../styles/forms.css';
import '../styles/TransactionsTable.css';
import '../styles/ImportTransactions.css';
import Icon from '@mdi/react';
import { mdiArrowRight, mdiClose, mdiCheck, mdiPencil, mdiDelete } from '@mdi/js';

const PHASE_LABELS = {
    parsing: 'Parsing statement…',
    resolving: 'Resolving subjects…',
    deduplicating: 'Checking duplicates…',
    committing: 'Committing…'
};

const ImportTransactions = ({ onCancel, onSuccess }) => {
    const [file, setFile] = useState(null);
    const [account, setAccount] = useState(null);
    const [format, setFormat] = useState('HSBC');

    const [isLoading, setIsLoading] = useState(false);
    const [phase, setPhase] = useState(null);
    const [progress, setProgress] = useState(null);
    const [results, setResults] = useState(null);
    const [showMappings, setShowMappings] = useState(false);
    const [simulate, setSimulate] = useState(true);
    const [error, setError] = useState(null);

    const [accounts, setAccounts] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [mappings, setMappings] = useState({});
    const [newMapping, setNewMapping] = useState({
        original: '',
        mappedOption: null,
        type: 'subject'
    });
    const [editType, setEditType] = useState('subject');

    const [editingIndex, setEditingIndex] = useState(null);
    const [editOriginal, setEditOriginal] = useState('');
    const [editMappedOption, setEditMappedOption] = useState(null);

    const fileInputRef = useRef(null);

    // Available CSV formats
    const formats = [
        { value: 'HSBC', label: 'HSBC' },
        { value: 'Revolut', label: 'Revolut' }
    ];

    useEffect(() => {
        const fetchSubjectMappings = async () => {
            try {
                const response = await api.get('/usersettings/import-mappings');
                var all_mappings = [];

                let index = 0;
                for (let [original, mappingData] of Object.entries(response.data)) {
                    if (typeof mappingData === 'object' && mappingData !== null) {
                        // New format with type
                        all_mappings[index] = {
                            original: original,
                            type: mappingData.type || 'subject',
                            subject_id: mappingData.type === 'subject' ? mappingData.id : null,
                            account_id: mappingData.type === 'account' ? mappingData.id : null
                        };
                    } else {
                        all_mappings[index] = {
                            original: original,
                            type: 'subject',
                            subject_id: Number(mappingData),
                            account_id: null
                        };
                    }
                    index++;
                }
                setMappings(all_mappings);
            } catch (error) {
                console.error('Error fetching import mappings:', error);
            }
        }

        fetchSubjectMappings();
    }, [subjects, accounts]);

    useEffect(() => {
        const fetchAccounts = async () => {
            try {
                const response = await api.get('/accounts');
                setAccounts(dayToDayAccounts(response.data.filter(a => a.is_open === true)).sort((a, b) => a.name.localeCompare(b.name)));
            } catch (error) {
                console.error('Error fetching accounts:', error);
            }
        };

        const fetchSubjects = async () => {
            try {
                const response = await api.get('/subjects');
                setSubjects(response.data.sort((a, b) => a.name.localeCompare(b.name)));
            } catch (error) {
                console.error('Error fetching subjects:', error);
            }
        };


        fetchAccounts();
        fetchSubjects();
    }, []);

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
        setResults(null);
    };

    const resolveMappedOption = async (option, type) => {
        if (type === 'subject' && option && option.isNew) {
            const ensured = await ensureSubject(option.label);
            setSubjects(prev => (prev.some(s => s.id === ensured.value)
                ? prev
                : [...prev, { id: ensured.value, name: ensured.label }]));
            return ensured;
        }
        return option;
    };

    const handleAddMapping = async () => {
        if (newMapping.original && newMapping.mappedOption) {
            const mapped = await resolveMappedOption(newMapping.mappedOption, newMapping.type);
            const numericKeys = Object.keys(mappings)
                .filter(k => !isNaN(k)) // Exclude non-numeric keys
                .map(Number); // Convert keys to numbers

            // Calculate the next key (default to 0 if no numeric keys exist)
            const nextKey = numericKeys.length > 0
                ? (Math.max(...numericKeys) + 1).toString()
                : "0";

            const newEntry = {
                original: newMapping.original,
                type: newMapping.type,
                subject_id: newMapping.type === 'subject' ? mapped.value : null,
                account_id: newMapping.type === 'account' ? mapped.value : null,
                subject_name: newMapping.type === 'subject' ? mapped.label : null,
                account_name: newMapping.type === 'account' ? mapped.label : null
            };

            setMappings(prev => ({
                ...prev,
                [nextKey]: newEntry
            }));
            setNewMapping({ original: '', mappedOption: null, type: 'subject' });
        }
    };

    const handleRemoveMapping = (index) => {
        const newMappings = { ...mappings };
        delete newMappings[index];
        setMappings(newMappings);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!file || !account) {
            alert('Please select a file and an account');
            return;
        }

        setIsLoading(true);
        setError(null);
        setResults(null);
        setPhase('parsing');
        setProgress(null);

        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('account_name', account);
            formData.append('format', format);

            const submit_mappings = {};

            // Convert mappings to the format expected by the backend
            Object.values(mappings).forEach(entry => {
                if (entry && entry.original) {
                    submit_mappings[entry.original] = {
                        type: entry.type,
                        id: entry.type === 'subject' ? entry.subject_id : entry.account_id
                    };
                }
            });

            formData.append('mappings', JSON.stringify(submit_mappings));
            formData.append('simulate', simulate);

            await streamImport(formData, {
                onPhase: ({ phase: nextPhase }) => setPhase(nextPhase),
                onProgress: ({ processed, total }) => setProgress({ processed, total }),
                onDone: (data) => {
                    setResults({ ...data, isSimulation: simulate });
                    setPhase(null);
                    setProgress(null);
                    onSuccess?.();
                },
                onError: ({ error: message }) => {
                    console.error('Error importing transactions:', message);
                    setError(message || 'An error occurred while importing transactions.');
                    setPhase(null);
                    setProgress(null);
                }
            });
        } catch (error) {
            console.error('Error importing transactions:', error);
            setError('An error occurred while importing transactions.');
        } finally {
            setIsLoading(false);
            setPhase(null);
            setProgress(null);
        }
    };

    const handleReset = () => {
        setFile(null);
        setAccount(null);
        setResults(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <div className="card">
            <h2 className="page-title">Import Statement</h2>

            {isLoading && (
                <div className="loading-overlay">
                    <div className="loading-panel">
                        <div className="loading-spinner"></div>
                        <p className="loading-phase">{PHASE_LABELS[phase] || 'Working…'}</p>
                        {phase === 'resolving' && progress && progress.total > 0 && (
                            <div className="progress-bar">
                                <div
                                    className="progress-bar__fill"
                                    style={{ width: `${Math.round((progress.processed / progress.total) * 100)}%` }}
                                ></div>
                            </div>
                        )}
                        {phase === 'resolving' && progress && progress.total > 0 && (
                            <p className="loading-progress-label">
                                {progress.processed} / {progress.total}
                            </p>
                        )}
                    </div>
                </div>
            )}

            <form onSubmit={handleSubmit} className="form">
                <div className="form-group-row settings">
                    <div className="form-group">
                        <label>Statement File</label>
                        <input
                            type="file"
                            accept=".csv,.pdf"
                            onChange={handleFileChange}
                            ref={fileInputRef}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Account</label>
                        <SearchableSelect
                            options={accounts.map(acc => ({ value: acc.name, label: acc.name }))}
                            onSelect={(option) => setAccount(option?.value)}
                            placeholder="Select an account"
                            value={account}
                        />
                    </div>

                    <div className="form-group">
                        <label>Format</label>
                        <Select
                            options={formats}
                            onSelect={(option) => setFormat(option?.value)}
                            value={format}
                        />
                    </div>

                    <button
                        type="button"
                        className="btn btn--secondary"
                        onClick={onCancel || handleReset}
                        disabled={isLoading}
                        hidden={true}
                    >
                        Reset
                    </button>
                </div>

                <div className="form-group">
                    <label className="toggle-switch">
                        <input
                            type="checkbox"
                            checked={showMappings}
                            onChange={(e) => setShowMappings(!showMappings)}
                        />
                        <span className="toggle-slider"></span>
                    </label>
                    <span className="toggle-label">Edit Subject Mappings</span>
                </div>

                {showMappings && (
                    <div className="form-group mappings-container">
                        <label>Subject Mappings</label>

                        <div style={{
                            maxHeight: '300px',
                            overflowY: 'auto',
                            border: '1px solid #ccc',
                            padding: '10px',
                            marginBottom: '10px'
                        }}>
                            {Object.entries(mappings).map(([index, mapping]) => (
                                <div className='form-group-row mapping-entry' key={index}>
                                    {editingIndex === index ? (
                                        <>
                                            <input
                                                type="text"
                                                value={editOriginal}
                                                onChange={(e) => setEditOriginal(e.target.value)}
                                                style={{ width: '200px' }}
                                            />

                                            <Icon path={mdiArrowRight} size={3} />

                                            <Select
                                                options={[
                                                    { value: 'subject', label: 'Subject' },
                                                    { value: 'account', label: 'Account' }
                                                ]}
                                                onSelect={(option) => {
                                                    setEditType(option.value);
                                                    setEditMappedOption(null);
                                                }}
                                                value={editType}
                                                style={{ width: '250px' }}
                                            />

                                            {editType === 'subject' ? (
                                                <SearchableSelect
                                                    options={subjects.map(subject => ({ value: subject.id, label: subject.name }))}
                                                    onSelect={(option) => setEditMappedOption(option)}
                                                    placeholder="Map to subject"
                                                    value={editMappedOption && editMappedOption.value ? editMappedOption.value : null}
                                                    allowCreate={true}
                                                />
                                            ) : (
                                                <SearchableSelect
                                                    options={accounts.map(account => ({ value: account.id, label: account.name }))}
                                                    onSelect={(option) => setEditMappedOption(option)}
                                                    placeholder="Map to account"
                                                    value={editMappedOption && editMappedOption.value ? editMappedOption.value : null}
                                                    allowCreate={false}
                                                />
                                            )}

                                            <button
                                                type="button"
                                                onClick={async () => {
                                                    if (!editMappedOption) {
                                                        alert('Please select a subject or account');
                                                        return;
                                                    }

                                                    const mapped = await resolveMappedOption(editMappedOption, editType);

                                                    const updatedMapping = {
                                                        original: editOriginal,
                                                        type: editType,
                                                        subject_id: editType === 'subject' ? mapped.value : null,
                                                        account_id: editType === 'account' ? mapped.value : null,
                                                        subject_name: editType === 'subject' ? mapped.label : null,
                                                        account_name: editType === 'account' ? mapped.label : null
                                                    };

                                                    setMappings(prev => ({ ...prev, [index]: updatedMapping }));
                                                    setEditingIndex(null);
                                                    setEditMappedOption(null); // Reset for next time
                                                }}
                                                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                                            >
                                                <Icon path={mdiCheck} size={0.8} style={{ color: 'green' }} />
                                            </button>

                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setEditMappedOption(null);
                                                    setEditingIndex(null);
                                                }}
                                                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                                            >
                                                <Icon path={mdiClose} size={0.8} style={{ color: 'red' }} />
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <label style={{ textAlign: 'left', width: '30%', fontWeight: 'bold' }}>
                                                {mapping.original}
                                            </label>

                                            <Icon path={mdiArrowRight} size={2} style={{ margin: '0 10px' }} />

                                            <label style={{ textAlign: 'left', width: '40%' }}>
                                                {mapping.type === 'subject'
                                                    ? subjects.find(s => s.id === mapping.subject_id)?.name || '(Unknown Subject)'
                                                    : "Transfer to " + accounts.find(a => a.id === mapping.account_id)?.name || '(Unknown Account)'
                                                }
                                            </label>

                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setEditOriginal(mapping.original);

                                                    if (mapping.type === 'subject') {
                                                        const subjectOption = subjects.find(s => s.id === mapping.subject_id);
                                                        setEditMappedOption(subjectOption ? { value: subjectOption.id, label: subjectOption.name } : null);
                                                    } else {
                                                        const accountOption = accounts.find(a => a.id === mapping.account_id);
                                                        setEditMappedOption(accountOption ? { value: accountOption.id, label: accountOption.name } : null);
                                                    }

                                                    setEditType(mapping.type);
                                                    setEditingIndex(index);
                                                }}
                                                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                                            >
                                                <Icon path={mdiPencil} size={0.8} />
                                            </button>

                                            <button
                                                type="button"
                                                onClick={() => handleRemoveMapping(index)}
                                                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                                            >
                                                <Icon path={mdiDelete} size={0.8} style={{ color: 'red' }} />
                                            </button>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="form-group-row" style={{ border: '1px solid #ccc', color: 'white', padding: '5px', alignItems: 'center' }}>
                            <div className="form-group">
                                <input
                                    type="text"
                                    placeholder="Original text"
                                    value={newMapping.original}
                                    onChange={(e) => setNewMapping({ ...newMapping, original: e.target.value })}
                                    style={{ width: '200px' }}
                                />
                            </div>

                            <Select
                                options={[
                                    { value: 'subject', label: 'Subject' },
                                    { value: 'account', label: 'Account' }
                                ]}
                                onSelect={(option) => setNewMapping({ ...newMapping, type: option.value })}
                                value={newMapping.type}
                                style={{ width: '100px' }}
                            />

                            {newMapping.type === 'subject' ? (
                                <SearchableSelect
                                    options={subjects.map(subject => ({ value: subject.id, label: subject.name }))}
                                    onSelect={(option) => setNewMapping({ ...newMapping, mappedOption: option })}
                                    placeholder="Map to subject"
                                    value={newMapping.mappedOption}
                                    allowCreate={true}
                                />
                            ) : (
                                <SearchableSelect
                                    options={accounts.map(account => ({ value: account.id, label: account.name }))}
                                    onSelect={(option) => setNewMapping({ ...newMapping, mappedOption: option })}
                                    placeholder="Map to account"
                                    value={newMapping.mappedOption}
                                />
                            )}

                            <button type="button" onClick={handleAddMapping}>
                                Add
                            </button>
                        </div>
                    </div>
                )
                }

                <div className="form-buttons">
                    <div className="form-group">
                        <label className="toggle-switch">
                            <input
                                type="checkbox"
                                checked={simulate}
                                onChange={(e) => setSimulate(e.target.checked)}
                            />
                            <span className="toggle-slider"></span>
                        </label>
                        <span className="toggle-label">Simulate Import</span>
                    </div>

                    <button
                        type="submit"
                        className="btn btn--primary"
                        disabled={isLoading || !file || !account}
                    >
                        {isLoading ? 'Importing...' : 'Import'}
                    </button>
                </div>
            </form >

            {(results || error) && (
                <div className="card">
                    <h2>{results?.isSimulation ? 'Simulation Results' : 'Import Results'}</h2>

                    {error && <p className='error'>{error}</p>}

                    {results && (
                        <div>
                            <p>Imported: {results.imported.length}</p>
                            <p>Skipped: {results.skipped.length}</p>
                            <p>Errors: {results.errors.length}</p>

                            {results.errors.length > 0 && (
                                <div className='form-group'>
                                    <div className="import-results-table">
                                        <table className="transaction-table">
                                            <thead>
                                                <th>Message</th>
                                            </thead>
                                            <tbody>
                                                {results.errors.map((error, index) => (
                                                    <tr key={index}>
                                                        <td>{error}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            <div className='form-group'>
                                <h3>Skipped Transactions</h3>
                                <div className="import-results-table">
                                    <table className="transaction-table" style={{ width: '100%' }}>
                                        <thead>
                                            <tr>
                                                <th>Date</th>
                                                <th>Amount</th>
                                                <th>Subject</th>
                                                <th>Category</th>
                                                <th>Notes</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {results.skipped.map((transaction, index) => (
                                                <tr key={index}>
                                                    <td>{transaction.date}</td>
                                                    <td>{transaction.amount}</td>
                                                    <td>{transaction.subject_name}</td>
                                                    <td>{transaction.category_name}</td>
                                                    <td>{transaction.notes}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className='form-group'>
                                <h3>Imported Transactions</h3>

                                <div className="import-results-table">
                                    <table className="transaction-table" style={{ width: '100%' }}>
                                        <thead>
                                            <tr>
                                                <th>Date</th>
                                                <th>Amount</th>
                                                <th>Subject</th>
                                                <th>Category</th>
                                                <th>Notes</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {results.imported.map((transaction, index) => (
                                                <tr key={index}>
                                                    <td>{transaction.date}</td>
                                                    <td>{transaction.amount}</td>
                                                    <td>{transaction.subject_name}</td>
                                                    <td>{transaction.category_name}</td>
                                                    <td>{transaction.notes}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div >
    );
};

export default ImportTransactions;